/**
 * Native API utility for Capacitor mobile apps.
 * Uses CapacitorHttp for native platforms to bypass WebView CORS restrictions.
 */

import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { getSession } from './nativeAuth';

// Get API URL - use production for native apps
const getApiUrl = () => {
  if (Capacitor.isNativePlatform()) {
    return 'https://api.shkalot.xyz';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5001';
};

/**
 * Make an API request that works on both web and native platforms.
 * On native platforms, uses CapacitorHttp to bypass CORS.
 * On web, uses standard fetch with credentials.
 * 
 * @param {string} endpoint - API endpoint (e.g., '/api/expenses')
 * @param {object} options - Fetch-like options (method, body, headers)
 * @returns {Promise<Response>} - Fetch-like response object
 */
export async function nativeFetch(endpoint, options = {}) {
  const API_URL = getApiUrl();
  const url = `${API_URL}${endpoint}`;
  
  if (Capacitor.isNativePlatform()) {
    // Use CapacitorHttp for native platforms
    const token = await getSession();
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const method = (options.method || 'GET').toUpperCase();
    
    let response;
    if (method === 'GET') {
      response = await CapacitorHttp.get({ url, headers });
    } else if (method === 'POST') {
      response = await CapacitorHttp.post({
        url,
        headers,
        data: options.body ? JSON.parse(options.body) : undefined,
      });
    } else if (method === 'PUT') {
      response = await CapacitorHttp.put({
        url,
        headers,
        data: options.body ? JSON.parse(options.body) : undefined,
      });
    } else if (method === 'PATCH') {
      response = await CapacitorHttp.patch({
        url,
        headers,
        data: options.body ? JSON.parse(options.body) : undefined,
      });
    } else if (method === 'DELETE') {
      response = await CapacitorHttp.delete({ url, headers });
    } else {
      response = await CapacitorHttp.request({
        url,
        method,
        headers,
        data: options.body ? JSON.parse(options.body) : undefined,
      });
    }
    
    // Return a fetch-like response object
    return {
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      headers: response.headers,
      json: async () => response.data,
      text: async () => typeof response.data === 'string' ? response.data : JSON.stringify(response.data),
      blob: async () => new Blob([JSON.stringify(response.data)]),
    };
  } else {
    // Use standard fetch for web
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    return fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });
  }
}

/**
 * Check if running on a native platform
 */
export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

export { getApiUrl };
